import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../components/Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DOCTOR_TITLE_LEVELS = new Set(['Doctorate Degree', 'MBChB']);

const getHighestEducationalLevel = (education) => {
  if (!education) return '';
  if (typeof education === 'string') return education;
  if (Array.isArray(education)) {
    const current = education.find((item) => item?.level || item?.highestEducationalLevel || item?.degree);
    return current?.level || current?.highestEducationalLevel || current?.degree || '';
  }
  return education.level || education.highestEducationalLevel || education.degree || '';
};

const getPractitionerDisplayName = (user) => {
  const surname = user?.lastName || user?.firstName || 'Practitioner';
  const level = getHighestEducationalLevel(user?.education);
  return DOCTOR_TITLE_LEVELS.has(level) ? `Dr. ${surname}` : surname;
};

// ═══════════════════════════════════════════════════════════════
// BREAKPOINTS
// ═══════════════════════════════════════════════════════════════
const bp = {
  xs: '360px',
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
};

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-14px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const float = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;
const ripple = keyframes`
  0%   { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.8); opacity: 0; }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const countUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const gradientFlow = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const arrowBounce = keyframes`
  0%,100% { transform: translateX(0); }
  50%     { transform: translateX(4px); }
`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;
const pulseGlow = keyframes`
  0%,100% { box-shadow: 0 0 0 0   rgba(14,165,233,0.25); }
  50%     { box-shadow: 0 0 0 10px rgba(14,165,233,0);    }
`;
const popIn = keyframes`
  0%   { transform: scale(0);    opacity: 0; }
  70%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;
const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const dashDraw = keyframes`
  from { stroke-dashoffset: 100; }
  to   { stroke-dashoffset: 0; }
`;
const breathe = keyframes`
  0%,100% { opacity: 0.6; transform: scale(1); }
  50%     { opacity: 1;   transform: scale(1.08); }
`;
const lineGrow = keyframes`
  from { stroke-dashoffset: 60; }
  to   { stroke-dashoffset: 0; }
`;

// ═══════════════════════════════════════════════════════════════
// LAYOUT
// ═══════════════════════════════════════════════════════════════
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f6ff;
  padding: 20px 16px 100px;
  position: relative;
  overflow-x: hidden;

  @media (min-width: ${bp.md}) {
    padding: 36px 24px 80px;
  }
`;

const Orb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(70px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;

  &.o1 {
    width: 420px; height: 420px;
    top: -150px; right: -100px;
    background: radial-gradient(circle, rgba(37,99,235,0.12), transparent 70%);
  }
  &.o2 {
    width: 340px; height: 340px;
    bottom: -80px; left: -60px;
    background: radial-gradient(circle, rgba(14,165,233,0.09), transparent 70%);
  }
  &.o3 {
    width: 260px; height: 260px;
    top: 45%; right: 4%;
    background: radial-gradient(circle, rgba(37,99,235,0.06), transparent 70%);
  }

  @media (max-width: ${bp.md}) {
    &.o1 { width: 280px; height: 280px; }
    &.o2 { width: 220px; height: 220px; }
    &.o3 { display: none; }
  }
`;

const Container = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeUp} 0.5s ease-out both;
`;

// ═══════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════
const WelcomeHero = styled.div`
  position: relative;
  background: linear-gradient(135deg, #0c2d48 0%, #1e40af 40%, #0369a1 70%, #0ea5e9 100%);
  background-size: 200% 200%;
  animation: ${gradientFlow} 8s ease infinite;
  border-radius: 20px;
  padding: 28px 24px;
  margin-bottom: 18px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(37,99,235,0.18);
  color: white;

  @media (min-width: ${bp.md}) {
    padding: 36px 40px;
    border-radius: 22px;
    margin-bottom: 22px;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  svg { width: 100%; height: 100%; position: absolute; }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  @media (max-width: ${bp.sm}) {
    flex-direction: column-reverse;
    gap: 14px;
    text-align: center;
  }
`;

const HeroLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const WelcomeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255,255,255,0.13);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 100px;
  padding: 4px 12px 4px 6px;
  margin-bottom: 12px;
  animation: ${fadeUp} 0.45s ease 0.1s backwards;
`;

const LiveDot = styled.span`
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #67e8f9;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: #67e8f9;
    animation: ${ripple} 2s ease-out infinite;
  }
`;

const BadgeLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  letter-spacing: 0.3px;
`;

const Greeting = styled.h1`
  font-size: clamp(1.2rem, 4vw, 1.75rem);
  font-weight: 800;
  color: white;
  margin: 0 0 6px;
  letter-spacing: -0.4px;
  line-height: 1.2;
  animation: ${fadeUp} 0.45s ease 0.15s backwards;

  .name { color: #67e8f9; }
`;

const WelcomeSub = styled.p`
  font-size: clamp(0.8rem, 2vw, 0.92rem);
  color: rgba(255,255,255,0.75);
  margin: 0;
  line-height: 1.55;
  max-width: 400px;
  animation: ${fadeUp} 0.45s ease 0.2s backwards;

  @media (max-width: ${bp.sm}) { max-width: 100%; }
`;

const HeroVisual = styled.div`
  flex-shrink: 0;
  width: 130px;
  height: 110px;
  animation: ${float} 6s ease-in-out infinite, ${fadeIn} 0.6s ease 0.3s backwards;

  @media (min-width: ${bp.md}) { width: 160px; height: 130px; }
  @media (max-width: ${bp.sm}) { width: 100px; height: 85px; }
`;

// ═══════════════════════════════════════════════════════════════
// STATS
// ═══════════════════════════════════════════════════════════════
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 18px;

  @media (min-width: ${bp.lg}) {
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    margin-bottom: 22px;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 18px 16px;
  border: 1.5px solid #e8f0fe;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  animation: ${fadeUp} 0.5s ease-out backwards;
  animation-delay: ${p => p.$delay || '0s'};
  transition: transform 0.3s cubic-bezier(0.22,1,0.36,1),
              box-shadow 0.3s cubic-bezier(0.22,1,0.36,1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.$accent || 'linear-gradient(90deg,#2563eb,#0ea5e9)'};
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 28px rgba(37,99,235,0.1);
    }
  }

  @media (min-width: ${bp.lg}) {
    padding: 22px 20px;
    border-radius: 18px;
  }
`;

const StatIconWrap = styled.div`
  width: 40px; height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: ${p => p.$bg || 'linear-gradient(135deg,#dbeafe,#bfdbfe)'};
  flex-shrink: 0;
  border: 0;
  background: transparent;
  font-family: inherit;

  svg {
    width: 19px; height: 19px;
    stroke: ${p => p.$color || '#2563eb'};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media (min-width: ${bp.lg}) {
    width: 44px; height: 44px;
    border-radius: 12px;
    margin-bottom: 14px;
    svg { width: 21px; height: 21px; }
  }
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 750;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 5px;

  @media (min-width: ${bp.lg}) { font-size: 0.68rem; }
`;

const StatValue = styled.div`
  font-size: clamp(1.4rem, 4vw, 1.9rem);
  font-weight: 800;
  color: #0c2d48;
  line-height: 1;
  margin-bottom: 5px;
  animation: ${countUp} 0.6s ease backwards;
  animation-delay: ${p => p.$delay || '0.3s'};
`;

const StatSub = styled.div`
  font-size: 0.7rem;
  color: ${p => p.$positive ? '#059669' : '#94a3b8'};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 11px; height: 11px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

// ═══════════════════════════════════════════════════════════════
// CONTENT GRID
// ═══════════════════════════════════════════════════════════════
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: ${bp.lg}) {
    grid-template-columns: 1fr 360px;
    gap: 18px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1.5px solid #e8f0fe;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  overflow: hidden;
  animation: ${fadeUp} 0.5s ease-out backwards;
  animation-delay: ${p => p.$delay || '0.3s'};

  @media (min-width: ${bp.lg}) { border-radius: 18px; }
`;

const CardHead = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .left {
    display: flex;
    align-items: center;
    gap: 9px;
  }

  .icon-wrap {
    width: 28px; height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$iconBg || 'linear-gradient(135deg,#dbeafe,#bfdbfe)'};
    flex-shrink: 0;

    svg {
      width: 14px; height: 14px;
      stroke: ${p => p.$iconColor || '#2563eb'};
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  h2 {
    font-size: 0.9rem;
    font-weight: 800;
    color: #0c2d48;
    margin: 0;
  }

  @media (min-width: ${bp.md}) {
    padding: 18px 20px;
    h2 { font-size: 0.95rem; }
    .icon-wrap { width: 30px; height: 30px; svg { width: 15px; height: 15px; } }
  }
`;

const ViewAllBtn = styled.a`
  font-size: 0.74rem;
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 9px;
  border-radius: 7px;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    width: 13px; height: 13px;
    stroke: currentColor; fill: none;
    stroke-width: 2.5; stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 0.2s ease;
  }

  @media (hover: hover) {
    &:hover {
      background: #dbeafe;
      svg { animation: ${arrowBounce} 0.6s ease infinite; }
    }
  }
`;



const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;

const FollowUpModal = styled.div`
  width: min(960px, 100%);
  max-height: min(82vh, 780px);
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
  animation: ${fadeUp} 0.22s ease both;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1.05rem;
    font-weight: 900;
  }

  p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 0.86rem;
    line-height: 1.5;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const ModalBtn = styled.button`
  border: 0;
  border-radius: 10px;
  padding: 9px 12px;
  font-weight: 800;
  font-size: 0.78rem;
  cursor: pointer;
  color: ${p => p.$primary ? '#ffffff' : '#334155'};
  background: ${p => p.$primary ? 'linear-gradient(135deg,#dc2626,#f97316)' : '#f1f5f9'};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${p => p.$primary ? '0 12px 24px rgba(220,38,38,0.22)' : 'none'};
    }
  }
`;

const ModalBody = styled.div`
  padding: 18px 20px 20px;
  overflow: auto;
`;

const FollowUpList = styled.div`
  display: grid;
  gap: 12px;
`;

const FollowUpItem = styled.div`
  border: 1px solid #fee2e2;
  border-radius: 16px;
  background: linear-gradient(135deg,#fff7ed,#ffffff 44%);
  padding: 14px;
  display: grid;
  gap: 10px;
`;

const FollowUpItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;

  .client {
    color: #0f172a;
    font-weight: 900;
    font-size: 0.95rem;
  }

  .meta {
    margin-top: 3px;
    color: #64748b;
    font-size: 0.78rem;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ReasonList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 0.82rem;
  line-height: 1.5;
`;

// ═══════════════════════════════════════════════════════════════
// TABLE — desktop
// ═══════════════════════════════════════════════════════════════
const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  display: none;

  @media (min-width: ${bp.md}) { display: block; }

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: #f8fafc; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 480px;

  th {
    padding: 10px 16px;
    text-align: left;
    font-size: 0.65rem;
    font-weight: 750;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    background: linear-gradient(90deg, #1e40af, #0369a1);
    color: rgba(255,255,255,0.9);
    white-space: nowrap;
    border-bottom: none;
  }

  td {
    padding: 13px 16px;
    font-size: 0.85rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.15s ease;
    animation: ${slideRight} 0.4s ease backwards;

    @media (hover: hover) { &:hover { background: #f8fbff; } }
    &:last-child td { border-bottom: none; }
  }

  tbody tr:nth-of-type(1) { animation-delay: 0.04s; }
  tbody tr:nth-of-type(2) { animation-delay: 0.08s; }
  tbody tr:nth-of-type(3) { animation-delay: 0.12s; }
  tbody tr:nth-of-type(4) { animation-delay: 0.16s; }
  tbody tr:nth-of-type(5) { animation-delay: 0.20s; }
`;

const ClientCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 34px; height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${p => p.$c1 || '#bfdbfe'}, ${p => p.$c2 || '#93c5fd'});
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 750;
  font-size: 0.68rem;
  color: white;
  flex-shrink: 0;
  letter-spacing: -0.3px;
  transition: transform 0.2s ease;

  @media (hover: hover) { tr:hover & { transform: scale(1.07); } }
`;

const MobileAvatar = styled(Avatar)`
  width: 42px; height: 42px;
  border-radius: 12px;
  font-size: 0.76rem;
`;

const ClientName = styled.span`
  font-weight: 650;
  color: #0c2d48;
  font-size: 0.86rem;
`;

const TestTag = styled.span`
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 0.74rem;
  font-weight: 650;
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
`;

const DateCell = styled.span`
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
`;

const RiskBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
  text-transform: capitalize;
  background: ${p=>p.level==='critical'?'#fee2e2':p.level==='high'?'#fff7ed':p.level==='moderate'?'#fefce8':'#f1f5f9'};
  color: ${p=>p.level==='critical'?'#991b1b':p.level==='high'?'#c2410c':p.level==='moderate'?'#a16207':'#64748b'};
  border: 1px solid ${p=>p.level==='critical'?'#fecaca':p.level==='high'?'#fed7aa':p.level==='moderate'?'#fef08a':'#e2e8f0'};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 750;
  white-space: nowrap;

  &::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  ${p => p.$type === 'paid' ? css`
    background: #d1fae5; color: #047857;
    border: 1px solid #a7f3d0;
    &::before { background: #047857; }
  ` : p.$type === 'completed' ? css`
    background: #dbeafe; color: #1d4ed8;
    border: 1px solid #93c5fd;
    &::before { background: #1d4ed8; }
  ` : p.$type === 'pending' ? css`
    background: #fef3c7; color: #d97706;
    border: 1px solid #fde68a;
    &::before { background: #d97706; }
  ` : css`
    background: #f1f5f9; color: #64748b;
    border: 1px solid #e2e8f0;
    &::before { background: #64748b; }
  `}
`;

// ═══════════════════════════════════════════════════════════════
// MOBILE CARD LIST
// ═══════════════════════════════════════════════════════════════
const ResultsList = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${bp.md}) { display: none; }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  animation: ${slideRight} 0.4s ease backwards;
  animation-delay: ${p => p.$delay || '0s'};
  transition: background 0.15s ease;

  &:last-child { border-bottom: none; }
  &:active { background: #f8fbff; }
`;

const ResultInfo = styled.div`
  flex: 1;
  min-width: 0;

  .client-name {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0c2d48;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .test-tag {
    font-size: 0.68rem;
    font-weight: 650;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    border-radius: 5px;
    padding: 2px 7px;
  }

  .date {
    font-size: 0.68rem;
    color: #94a3b8;
    font-weight: 500;
  }
`;

const ResultMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

// ═══════════════════════════════════════════════════════════════
// QUICK ACTIONS
// ═══════════════════════════════════════════════════════════════
const ActionsList = styled.div`
  padding: 10px 12px;
  display: none;

  @media (min-width: ${bp.lg}) { display: block; }
`;

const ActionItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 13px;
  background: transparent;
  border: 1.5px solid transparent;
  border-radius: 13px;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
  text-align: left;
  margin-bottom: 6px;
  animation: ${fadeUp} 0.4s ease backwards;
  animation-delay: ${p => p.$delay || '0s'};
  touch-action: manipulation;

  &:last-child { margin-bottom: 0; }

  @media (hover: hover) {
    &:hover {
      background: ${p => p.$hoverBg || '#f0f9ff'};
      border-color: ${p => p.$hoverBorder || '#bfdbfe'};
      transform: translateX(4px);

      .action-arrow svg { animation: ${arrowBounce} 0.6s ease infinite; }
      .action-icon { transform: scale(1.06); }
    }
  }

  &:active {
    transform: scale(0.98) translateX(2px);
    background: ${p => p.$hoverBg || '#f0f9ff'};
  }
`;

const ActionIcon = styled.div`
  width: 42px; height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.$bg || 'linear-gradient(135deg,#dbeafe,#bfdbfe)'};
  transition: transform 0.22s ease;

  svg {
    width: 19px; height: 19px;
    stroke: ${p => p.$color || '#2563eb'};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-size: 0.86rem;
    font-weight: 700;
    color: #0c2d48;
    margin-bottom: 2px;
  }

  .desc {
    font-size: 0.72rem;
    color: #94a3b8;
    font-weight: 500;
  }
`;

const ActionArrow = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 15px; height: 15px;
    stroke: currentColor; fill: none;
    stroke-width: 2.5; stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

// Mobile 2×2 grid
const ActionsMobileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px;

  @media (min-width: ${bp.lg}) { display: none; }
`;

const ActionGridItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 15px 13px;
  background: ${p => p.$hoverBg || '#f0f9ff'};
  border: 1.5px solid ${p => p.$borderColor || '#dbeafe'};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22,1,0.36,1);
  text-align: left;
  touch-action: manipulation;

  &:active {
    transform: scale(0.95);
    border-color: ${p => p.$activeColor || '#2563eb'};
  }

  .icon {
    width: 38px; height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$iconBg || 'linear-gradient(135deg,#dbeafe,#bfdbfe)'};

    svg {
      width: 18px; height: 18px;
      stroke: ${p => p.$iconColor || '#2563eb'};
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #0c2d48;
    line-height: 1.3;
  }

  .sub {
    font-size: 0.68rem;
    color: #94a3b8;
    font-weight: 500;
    margin-top: 1px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// EMPTY / LOADING / PENDING
// ═══════════════════════════════════════════════════════════════
const EmptyBlock = styled.div`
  padding: 44px 20px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease;

  .empty-icon {
    width: 56px; height: 56px;
    border-radius: 16px;
    background: linear-gradient(135deg,#dbeafe,#bfdbfe);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;

    svg {
      width: 26px; height: 26px;
      stroke: #2563eb; fill: none;
      stroke-width: 1.75;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  h4 {
    color: #0c2d48;
    font-size: 0.95rem;
    font-weight: 750;
    margin: 0 0 5px;
  }

  p {
    color: #94a3b8;
    font-size: 0.84rem;
    margin: 0 auto;
    max-width: 260px;
    line-height: 1.55;
  }
`;

const PendingView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 55vh;
  text-align: center;
  padding: 32px 20px;
  animation: ${fadeUp} 0.5s ease-out;
`;

const PendingOrb = styled.div`
  width: 96px; height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg,#dbeafe,#bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  animation: ${float} 4s ease-in-out infinite;
  position: relative;
  box-shadow: 0 12px 36px rgba(37,99,235,0.14);

  svg {
    width: 42px; height: 42px;
    stroke: #2563eb; fill: none;
    stroke-width: 2; stroke-linecap: round;
    stroke-linejoin: round;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -10px;
    border-radius: 50%;
    border: 2px dashed rgba(37,99,235,0.18);
    animation: ${spin} 12s linear infinite;
  }

  @media (max-width: ${bp.sm}) {
    width: 78px; height: 78px;
    svg { width: 34px; height: 34px; }
  }
`;

const PendingTitle = styled.h2`
  font-size: clamp(1.05rem, 3.5vw, 1.3rem);
  font-weight: 800;
  color: #0c2d48;
  margin: 0 0 10px;
`;

const PendingDesc = styled.p`
  font-size: clamp(0.82rem, 2vw, 0.9rem);
  color: #64748b;
  max-width: 340px;
  line-height: 1.65;
  margin: 0 0 18px;
`;

const LoadingView = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 55vh;
`;

const Spinner = styled.div`
  width: 42px; height: 42px;
  border: 3px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 18px 16px;
  border: 1.5px solid #e8f0fe;

  .skel {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400px 100%;
    animation: ${shimmer} 1.4s ease infinite;
    border-radius: 6px;
  }
  .skel-icon  { width: 40px; height: 40px; border-radius: 11px; margin-bottom: 12px; }
  .skel-label { width: 55%; height: 9px; margin-bottom: 9px; }
  .skel-value { width: 38%; height: 26px; margin-bottom: 7px; }
  .skel-sub   { width: 65%; height: 9px; }
`;

// ═══════════════════════════════════════════════════════════════
// SEARCH HIGHLIGHT
// ═══════════════════════════════════════════════════════════════
const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{
        background: '#fef9c3', color: '#92400e',
        borderRadius: 3, padding: '0 2px',
      }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// ANIMATED SVG ICONS
// ═══════════════════════════════════════════════════════════════

// Animated users icon — person 2 pulses in
const AnimatedUsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="7" r="4"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="r" values="4;4.5;4" dur="2.2s" repeatCount="indefinite" />
    </circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
    </path>
  </svg>
);

// Animated check-circle — polyline draws in
const AnimatedCheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-dasharray" values="0 60;60 60" dur="1.2s" fill="freeze" />
    </path>
    <polyline points="22 4 12 14.01 9 11.01"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      strokeDasharray="20" strokeDashoffset="20">
      <animate attributeName="stroke-dashoffset" values="20;0" dur="0.6s"
        begin="0.5s" fill="freeze" />
    </polyline>
  </svg>
);

// Animated clock — hand sweeps
const AnimatedClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <polyline points="12 6 12 12 16 14"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animateTransform attributeName="transform" type="rotate"
        values="0 12 12; 30 12 12; 0 12 12" dur="3s" repeatCount="indefinite" />
    </polyline>
  </svg>
);

// Animated trending-up — line draws then bounces
const AnimatedTrendingUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      strokeDasharray="40" strokeDashoffset="40">
      <animate attributeName="stroke-dashoffset" values="40;0" dur="1s" fill="freeze" />
    </polyline>
    <polyline points="17 6 23 6 23 12"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      strokeDasharray="16" strokeDashoffset="16">
      <animate attributeName="stroke-dashoffset" values="16;0" dur="0.5s"
        begin="0.8s" fill="freeze" />
    </polyline>
  </svg>
);

// Animated dollar — line pulses
const AnimatedDollarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="12" y1="1" x2="12" y2="23"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="stroke-opacity" values="1;0.5;1" dur="1.6s" repeatCount="indefinite" />
    </line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      strokeDasharray="38" strokeDashoffset="38">
      <animate attributeName="stroke-dashoffset" values="38;0" dur="1s" fill="freeze" />
    </path>
  </svg>
);

// Animated clipboard — lines type in
const AnimatedClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="8" y="2" width="8" height="4" rx="1"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="11" x2="16" y2="11"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="x2" values="12;16;16" dur="1.5s"
        begin="0.3s" fill="freeze" />
    </line>
    <line x1="12" y1="15" x2="16" y2="15"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="x2" values="12;16;16" dur="1.5s"
        begin="0.6s" fill="freeze" />
    </line>
    <line x1="8" y1="11" x2="8.01" y2="11"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="8" y1="15" x2="8.01" y2="15"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

// Animated send — polygon flies up-right
const AnimatedSendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="22" y1="2" x2="11" y2="13"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <polygon points="22 2 15 22 11 13 2 9 22 2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      fill="none">
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 2,-2; 0,0" dur="1.8s" repeatCount="indefinite" />
    </polygon>
  </svg>
);

// Animated bar chart — bars grow up
const AnimatedBarChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="18" y1="20" x2="18" y2="10"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeDasharray="10" strokeDashoffset="10">
      <animate attributeName="stroke-dashoffset" values="10;0" dur="0.6s" fill="freeze" />
    </line>
    <line x1="12" y1="20" x2="12" y2="4"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeDasharray="16" strokeDashoffset="16">
      <animate attributeName="stroke-dashoffset" values="16;0" dur="0.6s"
        begin="0.2s" fill="freeze" />
    </line>
    <line x1="6" y1="20" x2="6" y2="14"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeDasharray="6" strokeDashoffset="6">
      <animate attributeName="stroke-dashoffset" values="6;0" dur="0.5s"
        begin="0.4s" fill="freeze" />
    </line>
    <line x1="2" y1="20" x2="22" y2="20"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Animated wallet — shimmer pulse
const AnimatedWalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="1" y="4" width="22" height="16" rx="2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="1" y1="10" x2="23" y2="10"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="18" cy="15" r="1.5"
      stroke="currentColor" strokeWidth="1.5" fill="currentColor">
      <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
    </circle>
  </svg>
);

// Animated settings — inner circle orbits
const AnimatedSettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="3"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate"
        values="0 12 12;360 12 12" dur="8s" repeatCount="indefinite" />
    </circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated arrow-right
const AnimatedArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="5" y1="12" x2="19" y2="12"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="12 5 19 12 12 19"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Animated search — circle expands gently
const AnimatedSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="8"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="r" values="8;8.8;8" dur="2s" repeatCount="indefinite" />
    </circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Animated inbox — envelope flap opens
const AnimatedInboxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.5;1" dur="2.5s" repeatCount="indefinite" />
    </path>
  </svg>
);

// Animated hourglass — top sand drops
const AnimatedHourglassIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 22h14M5 2h14"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.3;1" dur="1.8s" repeatCount="indefinite" />
    </path>
    {/* Sand drop */}
    <circle cx="12" cy="9" r="1" fill="currentColor">
      <animate attributeName="cy" values="9;13;9" dur="1.8s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="1;0;1" dur="1.8s" repeatCount="indefinite" />
    </circle>
  </svg>
);

// Animated bolt — flickers
const AnimatedBoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"
      stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-opacity" values="1;0.4;1" dur="1.2s" repeatCount="indefinite" />
      <animate attributeName="fill-opacity" values="0;0.12;0" dur="1.2s" repeatCount="indefinite" />
    </polygon>
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// HERO ILLUSTRATION  (animated SVG — unchanged from original)
// ═══════════════════════════════════════════════════════════════
const DashboardIllustration = () => (
  <svg viewBox="0 0 170 140" fill="none">
    {/* Bars */}
    <rect x="15" y="80" width="18" height="40" rx="4"
      fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1">
      <animate attributeName="height" values="40;55;40" dur="3s" repeatCount="indefinite"/>
      <animate attributeName="y"      values="80;65;80" dur="3s" repeatCount="indefinite"/>
    </rect>
    <rect x="40" y="55" width="18" height="65" rx="4"
      fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.3)" strokeWidth="1">
      <animate attributeName="height" values="65;50;65" dur="3.5s" repeatCount="indefinite"/>
      <animate attributeName="y"      values="55;70;55" dur="3.5s" repeatCount="indefinite"/>
    </rect>
    <rect x="65" y="40" width="18" height="80" rx="4"
      fill="rgba(103,232,249,0.25)" stroke="rgba(103,232,249,0.4)" strokeWidth="1">
      <animate attributeName="height" values="80;90;80" dur="2.8s" repeatCount="indefinite"/>
      <animate attributeName="y"      values="40;30;40" dur="2.8s" repeatCount="indefinite"/>
    </rect>

    {/* Trend line */}
    <path d="M15 95 Q 40 70, 65 50 T 155 30"
      stroke="rgba(103,232,249,0.5)" strokeWidth="2" fill="none"
      strokeDasharray="200" strokeLinecap="round">
      <animate attributeName="stroke-dashoffset" values="200;0" dur="2s" fill="freeze"/>
    </path>

    {/* Floating card */}
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0,0; 0,-5; 0,0" dur="4s" repeatCount="indefinite"/>
      <rect x="100" y="35" width="58" height="42" rx="8"
        fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <line x1="110" y1="48" x2="140" y2="48"
        stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="110" y1="56" x2="130" y2="56"
        stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="148" cy="64" r="4"
        fill="rgba(103,232,249,0.5)" stroke="rgba(103,232,249,0.7)" strokeWidth="1">
        <animate attributeName="r" values="4;5;4" dur="2s" repeatCount="indefinite"/>
      </circle>
    </g>

    {/* Ambient dots */}
    <circle cx="24"  cy="28"  r="3" fill="rgba(255,255,255,0.15)">
      <animate attributeName="opacity" values="0.15;0.4;0.15" dur="3s" repeatCount="indefinite"/>
    </circle>
    <circle cx="145" cy="100" r="2" fill="rgba(103,232,249,0.3)">
      <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2.5s" repeatCount="indefinite"/>
    </circle>
    <line x1="10" y1="120" x2="160" y2="120"
      stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
  </svg>
);

const HeroBackgroundSVG = () => (
  <svg viewBox="0 0 800 200" preserveAspectRatio="none">
    <defs>
      <pattern id="dashGrid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40"
          fill="none" stroke="white" strokeOpacity="0.04" strokeWidth="1"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#dashGrid)"/>
    <circle cx="680" cy="30"  r="70" fill="white" fillOpacity="0.03"/>
    <circle cx="720" cy="160" r="45" fill="white" fillOpacity="0.04"/>
    <circle cx="80"  cy="170" r="90" fill="white" fillOpacity="0.02"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════
const avatarPalette = [
  ['#2563eb','#60a5fa'], ['#6366f1','#818cf8'], ['#ec4899','#f472b6'],
  ['#10b981','#34d399'], ['#f59e0b','#fbbf24'], ['#8b5cf6','#a78bfa'],
  ['#ef4444','#f87171'], ['#14b8a6','#2dd4bf'],
];
const getAvatarColors = (name) =>
  avatarPalette[(name || '').charCodeAt(0) % avatarPalette.length];
const getInitials = (f, l) =>
  `${(f || '')[0] || ''}${(l || '')[0] || ''}`.toUpperCase();
const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: '2-digit',
  });
};
const getRiskLabel = (risk) => risk?.level || 'low';

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isApproved } = useAuth();
  const { searchQuery }      = useSearch();
  const [stats, setStats]               = useState(null);
  const [recentResults, setRecentResults]     = useState([]);
  const [followUpCandidates, setFollowUpCandidates] = useState([]);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [modalFollowUps, setModalFollowUps] = useState([]);
  const [modalFollowUpsLoading, setModalFollowUpsLoading] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (isApproved) loadDashboard();
    else setLoading(false);
  }, [isApproved]);

  useEffect(() => {
    if (!searchQuery) { setFilteredResults(recentResults); return; }
    const q = searchQuery.toLowerCase();
    setFilteredResults(recentResults.filter(r => {
      const name   = `${r.Client?.firstName||''} ${r.Client?.lastName||''}`.toLowerCase();
      const test   = r.TestCatalog?.shortName?.toLowerCase() || '';
      const status = r.status?.toLowerCase() || '';
      const date   = r.completedAt
        ? new Date(r.completedAt).toLocaleDateString().toLowerCase() : '';
      return name.includes(q) || test.includes(q) ||
             status.includes(q) || date.includes(q);
    }));
  }, [searchQuery, recentResults]);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/practitioner/dashboard`);
      setStats(res.data.stats);
      setRecentResults(res.data.recentResults);
      setFollowUpCandidates(res.data.followUpCandidates || []);
      setFilteredResults(res.data.recentResults);
    } catch (e) {
      console.error('Dashboard load error:', e);
    } finally {
      setLoading(false);
    }
  };


  const openFollowUpModal = async () => {
    setShowFollowUpModal(true);
    setModalFollowUpsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/practitioner/follow-up-flags`);
      setModalFollowUps(res.data.results || []);
    } catch (e) {
      console.error('Follow-up modal load error:', e);
      setModalFollowUps(followUpCandidates);
    } finally {
      setModalFollowUpsLoading(false);
    }
  };

  const getTimeOfDay = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  };

  // ── Pending ──────────────────────────────────────────────────
  if (!isApproved) return (
    <PageWrapper>
      <Orb className="o1"/><Orb className="o2"/>
      <Container>
        <PendingView>
          <PendingOrb><AnimatedHourglassIcon /></PendingOrb>
          <PendingTitle>Account Pending Approval</PendingTitle>
          <PendingDesc>
            Your practitioner account is under review.
            You'll receive an email once approved.
          </PendingDesc>
          <StatusPill $type="pending">Status: {user?.approvalStatus}</StatusPill>
        </PendingView>
      </Container>
    </PageWrapper>
  );

  // ── Loading ──────────────────────────────────────────────────
  if (loading) return (
    <PageWrapper>
      <Orb className="o1"/>
      <Container>
        <div style={{
          height: 130, background: 'white',
          borderRadius: 18, marginBottom: 16,
          border: '1.5px solid #e8f0fe',
        }}/>
        <StatsGrid>
          {[0,1,2,3,4].map(i => (
            <SkeletonCard key={i}>
              <div className="skel skel-icon"/>
              <div className="skel skel-label"/>
              <div className="skel skel-value"/>
              <div className="skel skel-sub"/>
            </SkeletonCard>
          ))}
        </StatsGrid>
        <LoadingView><Spinner/></LoadingView>
      </Container>
    </PageWrapper>
  );

  // ── Actions config ───────────────────────────────────────────
  const actions = [
    {
      label: 'Send Test',     desc: 'Invite a client',
      icon:  <AnimatedSendIcon />,
      bg:    'linear-gradient(135deg,#dbeafe,#bfdbfe)', color: '#2563eb',
      hoverBg: '#f0f9ff', hoverBorder: '#bfdbfe', activeColor: '#2563eb',
      path: '/send-test',
    },
    {
      label: 'View Results',  desc: 'Check assessments',
      icon:  <AnimatedBarChartIcon />,
      bg:    'linear-gradient(135deg,#ede9fe,#ddd6fe)', color: '#7c3aed',
      hoverBg: '#f5f3ff', hoverBorder: '#ddd6fe', activeColor: '#7c3aed',
      path: '/results',
    },
    {
      label: 'Earnings',      desc: 'Revenue details',
      icon:  <AnimatedWalletIcon />,
      bg:    'linear-gradient(135deg,#d1fae5,#a7f3d0)', color: '#059669',
      hoverBg: '#ecfdf5', hoverBorder: '#a7f3d0', activeColor: '#059669',
      path: '/earnings',
    },
    {
      label: 'Settings',      desc: 'Manage profile',
      icon:  <AnimatedSettingsIcon />,
      bg:    'linear-gradient(135deg,#fef3c7,#fde68a)', color: '#d97706',
      hoverBg: '#fffbeb', hoverBorder: '#fde68a', activeColor: '#d97706',
      path: '/profile',
    },
  ];

  return (
    <PageWrapper>
      <Orb className="o1"/><Orb className="o2"/><Orb className="o3"/>
      <Container>

        {/* ── Hero ───────────────────────────────────────────── */}
        <WelcomeHero>
          <HeroBg><HeroBackgroundSVG/></HeroBg>
          <HeroContent>
            <HeroLeft>
              <WelcomeBadge>
                <LiveDot/>
                <BadgeLabel>Dashboard Overview</BadgeLabel>
              </WelcomeBadge>
              <Greeting>
                {getTimeOfDay()},{' '}
                <span className="name">{getPractitionerDisplayName(user)}</span>
              </Greeting>
              <WelcomeSub>
                Track clients, monitor assessments, and manage your workflow.
              </WelcomeSub>
            </HeroLeft>
            <HeroVisual><DashboardIllustration/></HeroVisual>
          </HeroContent>
        </WelcomeHero>

        {/* ── Stats ──────────────────────────────────────────── */}
        <StatsGrid>
          <StatCard $delay="0.05s" $accent="linear-gradient(90deg,#2563eb,#0ea5e9)">
            <StatIconWrap $bg="linear-gradient(135deg,#dbeafe,#bfdbfe)" $color="#2563eb">
              <AnimatedUsersIcon/>
            </StatIconWrap>
            <StatLabel>Total Clients</StatLabel>
            <StatValue $delay="0.35s">{stats?.totalClients || 0}</StatValue>
            <StatSub $positive>
              <AnimatedTrendingUpIcon/> Active
            </StatSub>
          </StatCard>

          <StatCard $delay="0.1s" $accent="linear-gradient(90deg,#10b981,#34d399)">
            <StatIconWrap $bg="linear-gradient(135deg,#d1fae5,#a7f3d0)" $color="#059669">
              <AnimatedCheckCircleIcon/>
            </StatIconWrap>
            <StatLabel>Completed</StatLabel>
            <StatValue $delay="0.4s">{stats?.totalTests || 0}</StatValue>
            <StatSub>All time</StatSub>
          </StatCard>

          <StatCard $delay="0.15s" $accent="linear-gradient(90deg,#f59e0b,#fbbf24)">
            <StatIconWrap $bg="linear-gradient(135deg,#fef3c7,#fde68a)" $color="#d97706">
              <AnimatedClockIcon/>
            </StatIconWrap>
            <StatLabel>Pending</StatLabel>
            <StatValue $delay="0.45s">{stats?.pendingTests || 0}</StatValue>
            <StatSub>Awaiting</StatSub>
          </StatCard>


          <StatCard $delay="0.18s" $accent="linear-gradient(90deg,#ef4444,#f97316)">
            <StatIconWrap $bg="linear-gradient(135deg,#fee2e2,#fed7aa)" $color="#dc2626">
              🚩
            </StatIconWrap>
            <StatLabel>Clients Flagged for Follow Up</StatLabel>
            <StatValue $delay="0.48s">{stats?.followUpFlags || 0}</StatValue>
            <StatSub>Priority review</StatSub>
          </StatCard>

          <StatCard $delay="0.2s" $accent="linear-gradient(90deg,#8b5cf6,#a78bfa)">
            <StatIconWrap $bg="linear-gradient(135deg,#ede9fe,#ddd6fe)" $color="#7c3aed">
              <AnimatedDollarIcon/>
            </StatIconWrap>
            <StatLabel>Earnings</StatLabel>
            <StatValue
              $delay="0.5s"
              style={{ fontSize: 'clamp(1.1rem, 3vw, 1.85rem)' }}
            >
              ₵{stats?.totalEarnings
                ? parseFloat(stats.totalEarnings).toFixed(2)
                : '0.00'}
            </StatValue>
            <StatSub $positive>
              <AnimatedTrendingUpIcon/> Revenue
            </StatSub>
          </StatCard>
        </StatsGrid>

        {/* ── Content grid ───────────────────────────────────── */}
        <ContentGrid>


          {followUpCandidates.length > 0 && (
            <Card $delay="0.22s">
              <CardHead
                $iconBg="linear-gradient(135deg,#fee2e2,#fed7aa)"
                $iconColor="#dc2626"
              >
                <div className="left">
                  <div className="icon-wrap">🚩</div>
                  <h2>Clients Flagged for Follow-up</h2>
                </div>
                <ViewAllBtn as="button" type="button" onClick={openFollowUpModal}>Review All</ViewAllBtn>
              </CardHead>
              <TableWrap>
                <DataTable>
                  <thead>
                    <tr><th>Client</th><th>Test</th><th>Flag</th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    {followUpCandidates.slice(0, 5).map(r => (
                      <tr key={r.id}>
                        <td>{r.Client?.firstName} {r.Client?.lastName}</td>
                        <td><TestTag>{r.TestCatalog?.shortName}</TestTag></td>
                        <td><RiskBadge level={getRiskLabel(r.followUpRisk)}>{getRiskLabel(r.followUpRisk)}</RiskBadge></td>
                        <td>{r.followUpRisk?.reasons?.[0] || r.followUpRisk?.recommendedAction}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </TableWrap>
            </Card>
          )}

          {/* Recent Results */}
          <Card $delay="0.25s">
            <CardHead
              $iconBg="linear-gradient(135deg,#dbeafe,#bfdbfe)"
              $iconColor="#2563eb"
            >
              <div className="left">
                <div className="icon-wrap"><AnimatedClipboardIcon/></div>
                <h2>Recent Results</h2>
              </div>
              <ViewAllBtn href="/#/results">
                View all <AnimatedArrowRightIcon/>
              </ViewAllBtn>
            </CardHead>

            {/* Desktop table */}
            <TableWrap>
              {filteredResults.length > 0 ? (
                <DataTable>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Test</th>
                      <th>Date</th>
                      <th>Follow-up</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map(r => {
                      const [c1, c2] = getAvatarColors(r.Client?.firstName);
                      return (
                        <tr key={r.id}>
                          <td>
                            <ClientCell>
                              <Avatar $c1={c1} $c2={c2}>
                                {getInitials(r.Client?.firstName, r.Client?.lastName)}
                              </Avatar>
                              <ClientName>
                                <HighlightText
                                  text={`${r.Client?.firstName} ${r.Client?.lastName}`}
                                  query={searchQuery}
                                />
                              </ClientName>
                            </ClientCell>
                          </td>
                          <td>
                            <TestTag>
                              <HighlightText
                                text={r.TestCatalog?.shortName}
                                query={searchQuery}
                              />
                            </TestTag>
                          </td>
                          <td><DateCell>{formatDate(r.completedAt)}</DateCell></td>
                          <td><RiskBadge level={getRiskLabel(r.followUpRisk)}>{getRiskLabel(r.followUpRisk)}</RiskBadge></td>
                          <td>
                            <StatusPill $type={r.isPaid ? 'paid' : 'completed'}>
                              {r.isPaid ? 'Paid' : 'Done'}
                            </StatusPill>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              ) : searchQuery ? (
                <EmptyBlock>
                  <div className="empty-icon"><AnimatedSearchIcon/></div>
                  <h4>No results found</h4>
                  <p>Nothing matched "{searchQuery}".</p>
                </EmptyBlock>
              ) : (
                <EmptyBlock>
                  <div className="empty-icon"><AnimatedInboxIcon/></div>
                  <h4>No results yet</h4>
                  <p>Send your first test to get started!</p>
                </EmptyBlock>
              )}
            </TableWrap>

            {/* Mobile card list */}
            <ResultsList>
              {filteredResults.length > 0
                ? filteredResults.map((r, i) => {
                    const [c1, c2] = getAvatarColors(r.Client?.firstName);
                    return (
                      <ResultRow key={r.id} $delay={`${i * 0.05}s`}>
                        <MobileAvatar $c1={c1} $c2={c2}>
                          {getInitials(r.Client?.firstName, r.Client?.lastName)}
                        </MobileAvatar>
                        <ResultInfo>
                          <div className="client-name">
                            <HighlightText
                              text={`${r.Client?.firstName} ${r.Client?.lastName}`}
                              query={searchQuery}
                            />
                          </div>
                          <div className="meta">
                            <span className="test-tag">
                              <HighlightText
                                text={r.TestCatalog?.shortName}
                                query={searchQuery}
                              />
                            </span>
                            <span className="date">{formatDate(r.completedAt)}</span>
                            <RiskBadge level={getRiskLabel(r.followUpRisk)}>{getRiskLabel(r.followUpRisk)}</RiskBadge>
                          </div>
                        </ResultInfo>
                        <ResultMeta>
                          <StatusPill $type={r.isPaid ? 'paid' : 'completed'}>
                            {r.isPaid ? 'Paid' : 'Done'}
                          </StatusPill>
                        </ResultMeta>
                      </ResultRow>
                    );
                  })
                : searchQuery ? (
                  <EmptyBlock>
                    <div className="empty-icon"><AnimatedSearchIcon/></div>
                    <h4>No results found</h4>
                    <p>Nothing matched "{searchQuery}".</p>
                  </EmptyBlock>
                ) : (
                  <EmptyBlock>
                    <div className="empty-icon"><AnimatedInboxIcon/></div>
                    <h4>No results yet</h4>
                    <p>Send your first test to get started!</p>
                  </EmptyBlock>
                )}
            </ResultsList>
          </Card>

          {/* Quick Actions */}
          <Card $delay="0.35s">
            <CardHead
              $iconBg="linear-gradient(135deg,#fef3c7,#fde68a)"
              $iconColor="#d97706"
            >
              <div className="left">
                <div className="icon-wrap"><AnimatedBoltIcon/></div>
                <h2>Quick Actions</h2>
              </div>
            </CardHead>

            {/* Desktop list */}
            <ActionsList>
              {actions.map((a, i) => (
                <ActionItem
                  key={a.path}
                  $delay={`${0.4 + i * 0.06}s`}
                  $hoverBg={a.hoverBg}
                  $hoverBorder={a.hoverBorder}
                  onClick={() => navigate(a.path)}
                >
                  <ActionIcon className="action-icon" $bg={a.bg} $color={a.color}>
                    {a.icon}
                  </ActionIcon>
                  <ActionContent>
                    <div className="title">{a.label}</div>
                    <div className="desc">{a.desc}</div>
                  </ActionContent>
                  <ActionArrow className="action-arrow">
                    <AnimatedArrowRightIcon/>
                  </ActionArrow>
                </ActionItem>
              ))}
            </ActionsList>

            {/* Mobile 2×2 grid */}
            <ActionsMobileGrid>
              {actions.map(a => (
                <ActionGridItem
                  key={a.path}
                  $hoverBg={a.hoverBg}
                  $borderColor={a.hoverBorder}
                  $activeColor={a.activeColor}
                  $iconBg={a.bg}
                  $iconColor={a.color}
                  onClick={() => navigate(a.path)}
                >
                  <div className="icon">{a.icon}</div>
                  <div>
                    <div className="label">{a.label}</div>
                    <div className="sub">{a.desc}</div>
                  </div>
                </ActionGridItem>
              ))}
            </ActionsMobileGrid>
          </Card>

        </ContentGrid>

        {showFollowUpModal && (
          <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="follow-up-modal-title">
            <FollowUpModal>
              <ModalHeader>
                <div>
                  <h3 id="follow-up-modal-title">Flagged clients needing follow up</h3>
                  <p>
                    Review the highest-priority flagged assessment results without leaving the dashboard.
                    Use the tracker page for organization and individual-link tracking.
                  </p>
                </div>
                <ModalActions>
                  <ModalBtn as="a" href="/#/flagged-follow-ups" $primary>Open tracker</ModalBtn>
                  <ModalBtn type="button" onClick={() => setShowFollowUpModal(false)}>Close</ModalBtn>
                </ModalActions>
              </ModalHeader>
              <ModalBody>
                {modalFollowUpsLoading ? (
                  <EmptyBlock>
                    <div className="empty-icon">🚩</div>
                    <h4>Loading flagged clients</h4>
                    <p>Fetching the full follow-up tracker list.</p>
                  </EmptyBlock>
                ) : modalFollowUps.length === 0 ? (
                  <EmptyBlock>
                    <div className="empty-icon">✅</div>
                    <h4>No flagged clients</h4>
                    <p>No clients currently need automated follow-up review.</p>
                  </EmptyBlock>
                ) : (
                  <FollowUpList>
                    {modalFollowUps.map(result => (
                      <FollowUpItem key={result.id}>
                      <FollowUpItemTop>
                        <div>
                          <div className="client">
                            {result.Client?.firstName} {result.Client?.lastName}
                          </div>
                          <div className="meta">
                            <span>{result.TestCatalog?.shortName || 'Assessment'}</span>
                            <span>{formatDate(result.completedAt)}</span>
                            <span>
                              {result.TestAssignment?.linkType === 'organization'
                                ? `Organization: ${result.TestAssignment?.organizationName || 'Unnamed'}`
                                : 'Individual link'}
                            </span>
                          </div>
                        </div>
                        <RiskBadge level={getRiskLabel(result.followUpRisk)}>{getRiskLabel(result.followUpRisk)}</RiskBadge>
                      </FollowUpItemTop>
                      <ReasonList>
                        {(result.followUpRisk?.reasons?.length ? result.followUpRisk.reasons : [result.followUpRisk?.recommendedAction]).map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ReasonList>
                      </FollowUpItem>
                    ))}
                  </FollowUpList>
                )}
              </ModalBody>
            </FollowUpModal>
          </ModalOverlay>
        )}
      </Container>
    </PageWrapper>
  );
};

export default Dashboard;
